// ─────────────────────────────────────────────────────────────
// ZIFEK — Multidevise
// Permet à un visiteur d'afficher l'équivalence des prix dans SA monnaie.
// La devise de référence reste TOUJOURS celle du marchand ; le visiteur
// peut choisir d'afficher la conversion dans une autre devise.
// Les taux viennent d'une API gratuite (open.er-api.com) avec repli sur
// des taux statiques lorsque l'API est indisponible.
// ─────────────────────────────────────────────────────────────

export interface Currency {
  code: string;
  label: string;
  symbol: string;
  flag: string;
}

export const CURRENCIES: Currency[] = [
  { code: 'MAD', label: 'Dirham marocain', symbol: 'DH', flag: '🇲🇦' },
  { code: 'EUR', label: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'USD', label: 'Dollar américain', symbol: '$', flag: '🇺🇸' },
  { code: 'GBP', label: 'Livre sterling', symbol: '£', flag: '🇬🇧' },
  { code: 'CHF', label: 'Franc suisse', symbol: 'CHF', flag: '🇨🇭' },
  { code: 'CAD', label: 'Dollar canadien', symbol: 'CA$', flag: '🇨🇦' },
  { code: 'AUD', label: 'Dollar australien', symbol: 'AU$', flag: '🇦🇺' },
  { code: 'DZD', label: 'Dinar algérien', symbol: 'DA', flag: '🇩🇿' },
  { code: 'TND', label: 'Dinar tunisien', symbol: 'DT', flag: '🇹🇳' },
  { code: 'EGP', label: 'Livre égyptienne', symbol: 'E£', flag: '🇪🇬' },
  { code: 'XOF', label: 'Franc CFA (UEMOA)', symbol: 'CFA', flag: '🌍' },
  { code: 'XAF', label: 'Franc CFA (CEMAC)', symbol: 'FCFA', flag: '🌍' },
  { code: 'GNF', label: 'Franc guinéen', symbol: 'FG', flag: '🇬🇳' },
  { code: 'CDF', label: 'Franc congolais', symbol: 'FC', flag: '🇨🇩' },
  { code: 'MRU', label: 'Ouguiya', symbol: 'UM', flag: '🇲🇷' },
  { code: 'AED', label: 'Dirham des Émirats', symbol: 'AED', flag: '🇦🇪' },
  { code: 'SAR', label: 'Riyal saoudien', symbol: 'SR', flag: '🇸🇦' },
  { code: 'QAR', label: 'Riyal qatari', symbol: 'QR', flag: '🇶🇦' },
  { code: 'TRY', label: 'Livre turque', symbol: '₺', flag: '🇹🇷' },
  { code: 'NGN', label: 'Naira', symbol: '₦', flag: '🇳🇬' },
  { code: 'GHS', label: 'Cedi', symbol: 'GH₵', flag: '🇬🇭' },
  { code: 'KES', label: 'Shilling kényan', symbol: 'KSh', flag: '🇰🇪' },
  { code: 'ZAR', label: 'Rand', symbol: 'R', flag: '🇿🇦' },
  { code: 'JPY', label: 'Yen', symbol: '¥', flag: '🇯🇵' },
  { code: 'CNY', label: 'Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'INR', label: 'Roupie indienne', symbol: '₹', flag: '🇮🇳' },
  { code: 'BRL', label: 'Réal', symbol: 'R$', flag: '🇧🇷' },
  { code: 'MXN', label: 'Peso mexicain', symbol: 'MX$', flag: '🇲🇽' },
];

// Taux statiques de repli : nombre d'unités de la devise pour 1 EUR.
const PER_EUR_STATIC: Record<string, number> = {
  EUR: 1,
  USD: 1.08,
  MAD: 10.8,
  GBP: 0.85,
  CHF: 0.95,
  CAD: 1.48,
  AUD: 1.62,
  DZD: 145,
  TND: 3.4,
  EGP: 53,
  XOF: 655.96,
  XAF: 655.96,
  GNF: 9300,
  CDF: 3000,
  MRU: 43,
  AED: 3.97,
  SAR: 4.05,
  QAR: 3.93,
  TRY: 37,
  NGN: 1700,
  GHS: 16.5,
  KES: 140,
  ZAR: 19.5,
  JPY: 168,
  CNY: 7.8,
  INR: 90,
  BRL: 6.1,
  MXN: 21,
};

// Taux effectifs (statiques par défaut, remplacés par les taux du marché).
let RATES: Record<string, number> = { ...PER_EUR_STATIC };
let RATES_ARE_LIVE = false;
let ratesPromise: Promise<boolean> | null = null;

const FX_STORAGE_KEY = 'zifek_fx_rates';
const FX_TTL_MS = 12 * 60 * 60 * 1000; // 12 h

/**
 * Normalise un code devise : gère les valeurs héritées/abréviations
 * (« Dhs », « DH », « dh » … → « MAD ») et met en majuscules.
 */
export function normalizeCurrency(code: string | null | undefined): string {
  const raw = (code || '').trim();
  if (!raw) return 'MAD';
  const up = raw.toUpperCase();
  if (up === 'DHS' || up === 'DH' || up === 'DIRHAM' || up === 'DIRHAMS') return 'MAD';
  if (up === 'FCFA' || up === 'XOF') return 'XOF';
  if (CURRENCIES.some((c) => c.code === up)) return up;
  // Code inconnu : on tente de le laisser tel quel (minuscule non supportée)
  return up;
}

/** Vrai si un code devise est reconnu par le convertisseur. */
export function isKnownCurrency(code: string): boolean {
  return Boolean(RATES[normalizeCurrency(code)]);
}

/**
 * Remplace les taux statiques par les taux du marché (relatifs à 1 EUR).
 * Les codes manquants gardent leur taux statique de repli.
 */
export function setLiveRates(rates: Record<string, number>): void {
  if (!rates || typeof rates !== 'object') return;
  const merged: Record<string, number> = { ...PER_EUR_STATIC };
  for (const [code, value] of Object.entries(rates)) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      merged[normalizeCurrency(code)] = value;
    }
  }
  merged.EUR = merged.EUR || 1;
  RATES = merged;
  RATES_ARE_LIVE = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('zifek-rates-loaded'));
  }
}

export function hasLiveRates(): boolean {
  return RATES_ARE_LIVE;
}

/** Instantané des taux actuellement utilisés (pour le journal superadmin). */
export function getRatesSnapshot(): { rates: Record<string, number>; live: boolean } {
  return { rates: { ...RATES }, live: RATES_ARE_LIVE };
}

/**
 * Métadonnées sur la source des taux : date de dernière mise à jour
 * (timestamp du cache) et source utilisée (API live ou taux de repli).
 */
export function getRatesMeta(): { updatedAt: number | null; source: 'live' | 'static' | 'cache' } {
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(FX_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { ts?: number };
        return { updatedAt: parsed?.ts ?? null, source: 'cache' };
      }
    } catch {
      /* cache illisible */
    }
  }
  return { updatedAt: null, source: RATES_ARE_LIVE ? 'live' : 'static' };
}

/** Date/heure de la dernière synchronisation réussie des taux (ou null). */
export function getRatesUpdatedAt(): number | null {
  return getRatesMeta().updatedAt;
}

/**
 * Charge les taux de change en direct (API gratuite, sans clé).
 * Met en cache dans localStorage avec une durée de vie de 12 h.
 * Ne rejette jamais : en cas d'échec, on garde les taux de repli.
 */
export function loadExchangeRates(force = false): Promise<boolean> {
  if (typeof fetch === 'undefined') return Promise.resolve(false);
  if (ratesPromise && !force) return ratesPromise;

  // Lecture du cache
  if (!force) {
    try {
      const raw = localStorage.getItem(FX_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { ts: number; rates: Record<string, number> };
        // Le cache n'est réutilisé que s'il date du MÊME JOUR et reste dans la
        // fenêtre TTL : les taux affichés sont ainsi toujours ceux du jour.
        const sameDay = (() => {
          if (!parsed?.ts) return false;
          const cached = new Date(parsed.ts);
          const now = new Date();
          return cached.getFullYear() === now.getFullYear()
            && cached.getMonth() === now.getMonth()
            && cached.getDate() === now.getDate();
        })();
        if (parsed?.rates && sameDay && Date.now() - parsed.ts < FX_TTL_MS) {
          setLiveRates(parsed.rates);
          ratesPromise = Promise.resolve(true);
          return ratesPromise;
        }
      }
    } catch {
      /* cache illisible */
    }
  }

  ratesPromise = fetch('https://open.er-api.com/v6/latest/EUR')
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      const rates = json?.rates as Record<string, number> | undefined;
      if (!rates) return false;
      setLiveRates(rates);
      try {
        localStorage.setItem(FX_STORAGE_KEY, JSON.stringify({ ts: Date.now(), rates }));
      } catch {
        /* stockage indisponible */
      }
      return true;
    })
    .catch(() => false);

  return ratesPromise;
}

const STORAGE_KEY = 'zifek_currency';

/** Devise d'affichage choisie par le visiteur ('' = monnaie du marchand). */
export function getDisplayCurrency(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** Change la devise d'affichage et prévient l'interface. */
export function setDisplayCurrency(code: string): void {
  try {
    if (code) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* stockage indisponible */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('zifek-currency-changed', { detail: { currency: code } }));
  }
}

export function currencyInfo(code: string): Currency {
  const normalized = normalizeCurrency(code);
  return (
    CURRENCIES.find((c) => c.code === normalized) || {
      code: normalized,
      label: normalized,
      symbol: normalized,
      flag: '🏳️',
    }
  );
}

/**
 * Convertit un montant d'une devise vers une autre.
 * Renvoie null si la conversion est impossible (devise inconnue).
 */
export function convertAmount(amount: number, from: string, to: string): number | null {
  if (!Number.isFinite(amount)) return null;
  const f = normalizeCurrency(from);
  const t = normalizeCurrency(to);
  if (!f || !t) return null;
  if (f === t) return amount;
  const fromRate = RATES[f];
  const toRate = RATES[t];
  if (!fromRate || !toRate) return null;
  return (amount / fromRate) * toRate;
}

/** Formate un montant dans une devise donnée. */
export function formatMoney(amount: number, code: string): string {
  const normalized = normalizeCurrency(code);
  const info = currencyInfo(normalized);
  const value = Number.isFinite(amount) ? amount : 0;
  const decimals = ['JPY', 'XOF', 'XAF', 'GNF', 'KES', 'NGN', 'CDF'].includes(normalized) ? 0 : 2;
  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  return `${formatted} ${info.symbol}`;
}

/**
 * Renvoie le texte d'équivalence (« ≈ 45,30 € ») entre la devise du marchand
 * et la devise d'affichage choisie. Renvoie null s'il n'y a rien à afficher.
 */
export function equivalenceText(amount: number, from: string, to: string): string | null {
  const f = normalizeCurrency(from);
  const t = normalizeCurrency(to);
  if (!to || !from || f === t) return null;
  const converted = convertAmount(amount, f, t);
  if (converted == null) return null;
  return `≈ ${formatMoney(converted, t)}`;
}

// Détecte un montant suivi d'une devise (« 350 MAD », « 350 DH », « 32 € »...).
const AMOUNT_RE = /(\d[\d\s\u00A0.,]*)\s*(MAD|DHS|DH|EUR|USD|GBP|CHF|CAD|AUD|DZD|TND|EGP|XOF|XAF|GNF|CDF|MRU|AED|SAR|QAR|TRY|NGN|GHS|KES|ZAR|JPY|CNY|INR|BRL|MXN|€|\$|£)/i;

const SYMBOL_TO_CODE: Record<string, string> = {
  '€': 'EUR',
  '$': 'USD',
  '£': 'GBP',
  DH: 'MAD',
};

/**
 * Ajoute l'équivalence de devise à un texte libre (ex. notification de commande)
 * lorsqu'un montant y est détecté. Renvoie le texte inchangé sinon.
 */
export function appendCurrencyEquivalence(
  message: string,
  nativeCurrency: string,
  displayCurrency: string,
): string {
  if (!message || !displayCurrency) return message;
  const native = normalizeCurrency(nativeCurrency);
  const display = normalizeCurrency(displayCurrency);
  if (display === native) return message;
  const match = message.match(AMOUNT_RE);
  if (!match) return message;
  const raw = match[1].replace(/[\s\u00A0]/g, '').replace(',', '.');
  const amount = parseFloat(raw);
  if (!Number.isFinite(amount)) return message;
  const code = normalizeCurrency(SYMBOL_TO_CODE[match[2].toUpperCase()] || match[2]);
  const from = isKnownCurrency(code) ? code : native;
  const eq = equivalenceText(amount, from, display);
  if (!eq) return message;
  return `${message} · ${eq}`;
}